/**
 * Dropfront grip SSOT resolve — weapon slot follows facing + anim progress.
 * Pixel coords are pack.size body space (currently 64×64; center from grips.json).
 * W = flip(E) on x: `x' = (size-1) - x`.
 */

import type { AnimState } from "../../art";
import type { CardinalDir } from "../../art/types";

export type GripPoint = [number, number];

export type GripAnimClips = {
  idle: GripPoint[];
  walk: GripPoint[];
  attack: GripPoint[];
};

export type GripFacingKit = {
  e: GripAnimClips;
  n: GripAnimClips;
  s: GripAnimClips;
};

export type GripPack = {
  size: number;
  center: [number, number];
  fps: { idle: number; walk: number; attack: number };
  byCharacter: Record<string, GripFacingKit>;
};

export type GripLocal = { x: number; y: number };

function clipFor(
  kit: GripFacingKit,
  facing: CardinalDir,
  anim: AnimState,
): { points: GripPoint[]; flipX: boolean } | null {
  const useFacing: "e" | "n" | "s" =
    facing === "w" ? "e" : facing === "n" || facing === "s" ? facing : "e";
  const flipX = facing === "w";
  const dir = kit[useFacing];
  if (!dir) return null;
  const key =
    anim === "walk" ? "walk" : anim === "attack" ? "attack" : "idle";
  const points = dir[key];
  if (!points?.length) return null;
  return { points, flipX };
}

function samplePoints(
  points: GripPoint[],
  timeSec: number,
  fps: number,
  loop: boolean,
): { x: number; y: number } {
  const n = points.length;
  if (n === 1) {
    return { x: points[0]![0], y: points[0]![1] };
  }
  const rate = Math.max(0.01, fps);
  const raw = timeSec * rate;
  let i0: number;
  let i1: number;
  let frac: number;
  if (loop) {
    const t = ((raw % n) + n) % n;
    i0 = Math.floor(t) % n;
    i1 = (i0 + 1) % n;
    frac = t - Math.floor(t);
  } else {
    const max = n - 1;
    const t = Math.max(0, Math.min(max, raw));
    i0 = Math.min(max, Math.floor(t));
    i1 = Math.min(max, i0 + 1);
    frac = i0 === i1 ? 0 : t - i0;
  }
  const a = points[i0]!;
  const b = points[i1]!;
  return {
    x: a[0] + (b[0] - a[0]) * frac,
    y: a[1] + (b[1] - a[1]) * frac,
  };
}

/**
 * Local offset from body center in display pixels
 * (sprite pixel delta × displaySize/size).
 */
export function resolveGripLocal(
  pack: GripPack | undefined,
  characterId: string,
  facing: CardinalDir | undefined,
  anim: AnimState,
  timeSec: number,
  displaySize: number,
): GripLocal | null {
  if (!pack || !facing) return null;
  const kit = pack.byCharacter[characterId];
  if (!kit) return null;
  const clip = clipFor(kit, facing, anim);
  if (!clip) return null;
  const fpsKey =
    anim === "walk" ? "walk" : anim === "attack" ? "attack" : "idle";
  const fps = pack.fps?.[fpsKey] ?? 6;
  const loop = anim !== "attack";
  let { x, y } = samplePoints(clip.points, timeSec, fps, loop);
  if (clip.flipX) {
    x = pack.size - 1 - x;
  }
  const [cx, cy] = pack.center;
  const scale = displaySize / Math.max(1, pack.size);
  return {
    x: (x - cx) * scale,
    y: (y - cy) * scale,
  };
}

/** Pixel grip for generators (no display scale). */
export function resolveGripPixel(
  pack: GripPack,
  characterId: string,
  facing: "e" | "n" | "s",
  anim: "idle" | "walk" | "attack",
  frameIndex: number,
): GripPoint | null {
  const kit = pack.byCharacter[characterId];
  const points = kit?.[facing]?.[anim];
  if (!points?.length) return null;
  const i = Math.max(0, Math.min(points.length - 1, frameIndex));
  return points[i]!;
}
